import { useState } from "react";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent, SxProps } from "@mui/material";

export type DropdownItem<T> = {
  value: T;
  label: string;
};

interface DropdownProps<T> {
  /** the options in the drop down that will be selectable by the user */
  options: Array<DropdownItem<T>>;
  /** the type specific value choosen by the user or set by default */
  value: T;
  /** the handler function that executes when the user selects an option from the drop */
  onChange: (e: SelectChangeEvent<T>) => void;
  /** the name of the dropdown shown to the user */
  label?: string;
  /** the unique id for html of the drop down */
  id: string;
  /** an [OPTIONAL] message to the user. when error=true then this will show as error */
  helperText?: string;
  /** an [OPTIONAL] value that indicates if the drop down has an error */
  error?: boolean;
  /** an [OPTIONAL] parameter indicating that this dropdown is required to be answered */
  required?: boolean;
  /** the style of the dropdown at the Select and FormControl components */
  sx?: {
    select?: SxProps;
    formControl?: SxProps;
  };
  /** create a placeholder value in the dropdown */
  placeholder?: string;
  /** allow an adornment at the start of the drop down */
  startAdornment?: any;
  /** the name of the dropdown shown to the user */
  name?: string;
}

const Dropdown = <T extends string | number>({
  placeholder,
  options,
  value,
  onChange,
  label,
  helperText,
  error,
  required = false,
  id,
  sx,
  startAdornment,
  name,
}: DropdownProps<T>) => {
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = () => {
    if (!open) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e: SelectChangeEvent<T>) => {
    onChange(e);
  };

  return (
    <FormControl
      fullWidth
      error={error}
      sx={sx?.formControl}
    >
      {label && <InputLabel id={`${id}-label`}>{label}</InputLabel>}
      <Select<T>
        displayEmpty={typeof placeholder === "string"}
        label={label}
        labelId={`${id}-label`}
        value={value}
        onChange={handleChange}
        required={required}
        id={id}
        sx={sx?.select}
        onClick={handleOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        open={open}
        startAdornment={startAdornment}
        name={name}
        inputProps={{
          name: name,
        }}
      >
        {placeholder && (
          <MenuItem
            disabled
            value=""
          >
            {placeholder}
          </MenuItem>
        )}
        {options.map((opt, i) => (
          <MenuItem
            key={i}
            value={opt.value}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </FormControl>
  );
};
/**
 * A base component to create a basic dropdown for a form
 *
 * @component Dropdown
 * @interface DropdownProps
 * @property {Array<DropdownItem<T>>} items - the options in the drop down that will be selectable by the user
 * @property {T} value - the handler function that executes when the user selects an option from the drop
 * @property {(value: T) => void} handleChange -
 * @property {string} label - the name of the dropdown shown to the user
 * @property {string} id - the unique id for html of the drop down
 * @property {string} helperText - an [OPTIONAL] message to the user. when error=true then this will show as error
 * @property {boolean} error - an [OPTIONAL] value that indicates if the drop down has an error
 * @property {boolean} required - an [OPTIONAL] parameter indicating that this dropdown is required to be answered
 * @property {SxProps} sx - the style of the dropdown at the Select and FormControl components
 * @property {string} placeholder - create a placeholder value in the dropdown
 * @property {any} startAdornment - allow an adornment at the start of the drop down
 */
export default Dropdown;
